// Need to use the React-specific entry point to import createApi
import { createApi } from "@reduxjs/toolkit/query/react";
import { FleetTrack2AuthenticatePayload } from "../types/Admin";
import {
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  ForgotPasswordVerifyOtpPayload,
  ForgotPasswordVerifyOtpResponse,
  LegacyVerifyEmailOtpResponse,
  LoginPayload,
  LoginResponse,
  LoginSsoCredsPayload,
  LoginSsoCredsPayloadPartial,
  LoginSsoCredsResponse,
  LoginSsoVerifyPayload,
  OnboardInvitedUserPayload,
  OnboardInvitedUserResponse,
  OrganizationInvitationPayload,
  OrganizationInvitationResponseSuccess,
  ResendEmailOtpPayload,
  ResendEmailOtpResponse,
  VerifyEmailOtpPayload,
} from "../types/Onboarding";
import { appBaseQuery, baseQueryWithAuthHandling } from "../utils/utils";
import { API_METHODS } from "./constants";
import { API_ENDPOINTS, APP_APIS_ENV } from "./endpoints";
import { handleAuthErrorCode } from "./errorCodes";

// Define a service using a base URL and expected endpoints
export const AuthAPIs = createApi({
  reducerPath: "authAPI",
  // tagTypes: [],
  baseQuery: (args, api, extraOptions) =>
    baseQueryWithAuthHandling(appBaseQuery.noAuthJson, args, api, extraOptions),
  endpoints: (builder) => ({
    // login
    login: builder.mutation<
      LoginResponse | LegacyVerifyEmailOtpResponse,
      LoginPayload
    >({
      query: (params: LoginPayload) => {
        return {
          url: API_ENDPOINTS.LOGIN,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: async (
        data: LoginResponse | LegacyVerifyEmailOtpResponse
      ) => {
        return data;
      },
    }),

    // login SSO creds
    loginSsoCreds: builder.mutation<
      LoginSsoCredsResponse,
      LoginSsoCredsPayloadPartial
    >({
      query: (params: LoginSsoCredsPayloadPartial) => {
        return {
          url: API_ENDPOINTS.LOGIN_SSO_CREDS,
          method: API_METHODS.POST,
          body: {
            ...params,
            env: APP_APIS_ENV,
          } satisfies LoginSsoCredsPayload,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: async (data: LoginSsoCredsResponse) => {
        return data;
      },
    }),

    // sso verify - send idToken obtained from Azure AD to backend for verification and login
    loginSsoVerify: builder.mutation<
      LoginResponse | LegacyVerifyEmailOtpResponse,
      LoginSsoVerifyPayload
    >({
      query: (params: LoginSsoVerifyPayload) => {
        return {
          url: API_ENDPOINTS.LOGIN_SSO_VERIFY,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: async (
        data: LoginResponse | LegacyVerifyEmailOtpResponse
      ) => {
        return data;
      },
    }),

    // resend email otp
    resendEmailOtp: builder.mutation<
      ResendEmailOtpResponse,
      ResendEmailOtpPayload
    >({
      query: (params: ResendEmailOtpPayload) => {
        return {
          url: API_ENDPOINTS.RESEND_EMAIL_OTP,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: async (data: ResendEmailOtpResponse) => {
        return data;
      },
    }),

    // verify email otp
    verifyEmailOtp: builder.mutation<
      LegacyVerifyEmailOtpResponse,
      VerifyEmailOtpPayload
    >({
      query: (params: VerifyEmailOtpPayload) => {
        return {
          url: API_ENDPOINTS.VERIFY_EMAIL_OTP,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: async (data: LegacyVerifyEmailOtpResponse) => {
        return data;
      },
    }),

    // forgot password
    forgotPassword: builder.mutation<
      ForgotPasswordResponse,
      ForgotPasswordPayload
    >({
      query: (params: ForgotPasswordPayload) => {
        console.log(params);
        return {
          url: API_ENDPOINTS.FORGOT_PASSWORD,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: async (data: ForgotPasswordResponse) => {
        return data;
      },
    }),

    // forgot password verify otp (i.e. reset password)
    forgotPasswordVerifyOtp: builder.mutation<
      ForgotPasswordVerifyOtpResponse,
      ForgotPasswordVerifyOtpPayload
    >({
      query: (params: ForgotPasswordVerifyOtpPayload) => {
        return {
          url: API_ENDPOINTS.FORGOT_PASSWORD_VERIFY_OTP,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: async (data: ForgotPasswordVerifyOtpResponse) => {
        return data;
      },
    }),

    // show invite user
    organizationShowInviteUser: builder.query<
      OrganizationInvitationResponseSuccess,
      OrganizationInvitationPayload
    >({
      query: ({ invitation_token }) => {
        return {
          url: API_ENDPOINTS.ORGANIZATION.SHOW_INVITE_USER(invitation_token),
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: OrganizationInvitationResponseSuccess) => {
        return response;
      },
    }),

    // onboard invited user
    onboardInvitedUser: builder.mutation<
      OnboardInvitedUserResponse,
      OnboardInvitedUserPayload
    >({
      query: (params: OnboardInvitedUserPayload) => {
        return {
          url: API_ENDPOINTS.ORGANIZATION.ONBOARD_INVITED_USER,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: async (data: OnboardInvitedUserResponse) => {
        return data;
      },
    }),

    // authenticate with inbound link from FT 1.0
    authenticateToFleetTrack2: builder.mutation<
      LoginResponse | LegacyVerifyEmailOtpResponse,
      FleetTrack2AuthenticatePayload
    >({
      query: (params: FleetTrack2AuthenticatePayload) => {
        return {
          url: API_ENDPOINTS.FT2_0_AUTH,
          method: API_METHODS.POST,
          body: params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (
        response: LoginResponse | LegacyVerifyEmailOtpResponse
      ) => {
        return response;
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLoginMutation,
  useLoginSsoCredsMutation,
  useLoginSsoVerifyMutation,
  useResendEmailOtpMutation,
  useVerifyEmailOtpMutation,
  useForgotPasswordMutation,
  useForgotPasswordVerifyOtpMutation,
  useLazyOrganizationShowInviteUserQuery,
  useOnboardInvitedUserMutation,
  useAuthenticateToFleetTrack2Mutation,
} = AuthAPIs;
